/* global navigator document */
import { useEffect, useRef } from 'react'
import MobileDetect from 'mobile-detect'

// Wake Lock only matters on mobile: desktop screens aren't at risk of
// sleeping during gameplay the way a phone/tablet is. Requesting a wake
// lock on desktop would be harmless but pointless, so we gate on device
// type to avoid the unnecessary API call.
export function isMobileDevice() {
  if (typeof navigator === 'undefined') return false
  if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
    return navigator.userAgentData.mobile
  }
  return !!new MobileDetect(navigator.userAgent).mobile()
}

export function useWakeLock(active) {
  const wakeLockRef = useRef(null)
  const unmountedRef = useRef(false)

  useEffect(() => {
    unmountedRef.current = false

    if (!active || !isMobileDevice() || !('wakeLock' in navigator)) {
      return undefined
    }

    const requestLock = () => {
      navigator.wakeLock
        .request('screen')
        .then((lock) => {
          if (unmountedRef.current) {
            lock.release().catch(() => {})
            return
          }
          wakeLockRef.current = lock
        })
        .catch(() => {})
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) {
        requestLock()
      }
    }

    requestLock()
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      unmountedRef.current = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {})
        wakeLockRef.current = null
      }
    }
  }, [active])
}
